import React, { useMemo } from 'react';
import {
  Steps,
  Button,
  ButtonType,
  Row,
  Col
} from 'syngenta-digital-cropwise-react-ui-kit';
import ChevronLeftIcon from 'assets/chevron_left.svg?react';
import './progressStepper.css';
import { useFormikContext } from 'formik';

export interface ProgressStepperStep {
  name: string;
  component: React.ComponentType<any>;
  initialValues: Record<string, any>;
  prerequisites?: string[];
  optional?: boolean;
  showButton?: boolean;
  handleSave?: () => void | Promise<void>;
  saveButtonContent?: React.ReactNode;
  disabled?: boolean;
  // Overrides the auto-generated "Next: <next step name>" / "Back: <previous
  // step name>" footer button text for this step, when the step name shown
  // in the header nav differs from the label the footer button should show.
  nextButtonLabel?: string;
  backButtonLabel?: string;
}

export interface ProgressStepperProps {
  steps: ProgressStepperStep[];
  stepperName: string;
  currentStep: number;
  onStepChange: (stepIndex: number) => void;
  onCancel?: () => void;
  disableSaveButton?: boolean;
  disableStepButtons?: boolean;
  saveButtonContent?: React.ReactNode;
  maxContentWidth?: number;
  // Header grid split (of 24 cols) between the stepper title and the steps row.
  // Defaults match the Applications & Work Orders layout; Invoices, with fewer
  // steps, uses an even 12/12 split.
  titleColSpan?: number;
  stepsColSpan?: number;
}

const ProgressStepper: React.FC<ProgressStepperProps> = ({
  steps,
  stepperName,
  currentStep,
  disableSaveButton = false,
  disableStepButtons = false,
  onStepChange,
  onCancel = () => {},
  maxContentWidth = 1200,
  titleColSpan = 7,
  stepsColSpan = 17
}) => {
  const { errors } = useFormikContext<Record<string, any>>();
  const StepComponent = steps[currentStep].component;

  // True when the step the user is currently on has validation errors in its
  // own fields (e.g. a required field was cleared).
  const currentStepFields = Object.keys(steps[currentStep].initialValues);
  const currentStepHasError = Object.keys(errors).some(key =>
    currentStepFields.includes(key)
  );

  // Disables the "Next" button if there are errors in the current step's fields
  const isNextDisabled = () => disableStepButtons || currentStepHasError;

  const hasError = useMemo(() => {
    return errors && Object.keys(errors).length > 0;
  }, [errors]);

  const isStepDisabled = (index: number) => {
    if (disableStepButtons || steps[index].disabled) return true;
    // The current step and any earlier step are always reachable — going back
    // is never blocked by validation, matching the Back button.
    if (index <= currentStep) return false;
    // Later steps are locked while the current step is invalid (mirrors the
    // disabled Next button so the stepper can't bypass it, e.g. after clearing
    // a required field), and otherwise gated by their own prerequisites.
    if (currentStepHasError) return true;
    return Object.keys(errors).some(key =>
      (steps[index].prerequisites ?? []).includes(key)
    );
  };

  return (
    <div className="w-full h-full flex flex-col min-h-0 relative">
      {/* Sticky Header */}
      <Row
        prefixCls="syt-antd-row"
        justify="center"
        className="bg-white h-56px min-h-56px items-center sticky top-0 z-20 border-b border-neutral-20 px-4"
      >
        <div
          style={{ maxWidth: maxContentWidth, margin: '0 auto', width: '100%' }}
          className="flex w-full py-2"
        >
          <Col
            prefixCls="syt-antd-col"
            className="flex items-center"
            span={titleColSpan}
          >
            <span
              className="text-lg font-semibold text-gray-900"
              style={{
                fontSize: '20px',
                color: '#14151C',
                fontWeight: 600,
                lineHeight: '28px'
              }}
            >
              {stepperName}
            </span>
          </Col>
          <Col
            prefixCls="syt-antd-col"
            className="w-full mx-auto flex items-center justify-between"
            span={stepsColSpan}
          >
            <Steps
              current={currentStep}
              onChange={idx => {
                if (!isStepDisabled(idx)) onStepChange(idx);
              }}
              className="application-work-order-progress-stepper"
              size="small"
            >
              {steps.map((step, index) => (
                <Steps.Step
                  key={step.name}
                  disabled={isStepDisabled(index)}
                  title={
                    <div
                      className="custom-step-label"
                      style={{
                        display: step.optional ? 'flex' : 'block',
                        flexDirection: step.optional ? 'column' : undefined,
                        alignItems: step.optional ? 'flex-start' : undefined,
                        justifyContent: step.optional ? 'center' : undefined,
                        minHeight: step.optional ? 32 : undefined,
                        width: '100%'
                      }}
                    >
                      <span
                        style={{
                          textAlign: 'left',
                          lineHeight: '20px',
                          width: '100%',
                          fontSize: '14px'
                        }}
                      >
                        {step.name}
                      </span>
                      {step.optional && (
                        <span
                          style={{
                            fontSize: '12px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '20px',
                            textAlign: 'left',
                            width: '100%'
                          }}
                        >
                          optional
                        </span>
                      )}
                    </div>
                  }
                />
              ))}
            </Steps>
          </Col>
        </div>
      </Row>
      {/* Step Content */}
      <div className="flex flex-1 justify-center min-h-0 overflow-y-auto p-4">
        <div className="w-full" style={{ maxWidth: maxContentWidth }}>
          <StepComponent />
        </div>
      </div>
      {/* Sticky Footer */}
      <div className="bg-white h-56px flex items-center w-full sticky bottom-0 z-20 py-2 px-4 border-b border-t border-neutral-20 gap-4">
        <div
          style={{ maxWidth: maxContentWidth, margin: '0 auto', width: '100%' }}
          className="flex w-full items-center"
        >
          <Button
            type={ButtonType.outline}
            size="large"
            style={{
              padding: '10px 20px',
              fontSize: '14px'
            }}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <div className="flex gap-4 ml-auto">
            {currentStep > 0 && (
              <Button
                type={ButtonType.outline}
                style={{ minWidth: 213, width: 'auto', fontSize: '14px' }}
                size="large"
                onClick={() => onStepChange(Math.max(currentStep - 1, 0))}
                disabled={currentStep === 0 || disableStepButtons}
              >
                <div className="flex items-center justify-center w-full">
                  <ChevronLeftIcon className="mr-2" />
                  <span>
                    Back
                    {currentStep > 0
                      ? `: ${steps[currentStep].backButtonLabel ??
                          steps[currentStep - 1].name}`
                      : ''}
                  </span>
                </div>
              </Button>
            )}
            {currentStep < steps.length - 1 && (
              <Button
                data-testid="next-step-button"
                type={ButtonType.primary}
                ghost
                style={{ minWidth: 213, width: 'auto', fontSize: '14px' }}
                size="large"
                onClick={() => {
                  const nextStep = currentStep + 1;
                  onStepChange(Math.min(nextStep, steps.length - 1));
                }}
                disabled={isNextDisabled()}
              >
                <div className="flex items-center justify-center w-full">
                  <span>
                    Next
                    {currentStep < steps.length - 1
                      ? `: ${steps[currentStep].nextButtonLabel ??
                          steps[currentStep + 1].name}`
                      : ''}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="18"
                    viewBox="0 0 19 18"
                    fill="none"
                    className="ml-2"
                  >
                    <path
                      d="M6.71973 5.5575L10.1547 9L6.71973 12.4425L7.77723 13.5L12.2772 9L7.77723 4.5L6.71973 5.5575Z"
                      fill={
                        currentStep === steps.length - 1 || isNextDisabled()
                          ? '#C2C7D0'
                          : '#14803C'
                      }
                    />
                  </svg>
                </div>
              </Button>
            )}
            <Button
              type={ButtonType.primary}
              size="large"
              className="flex items-center justify-center"
              onClick={() => {
                if (steps[currentStep]?.handleSave) {
                  steps[currentStep].handleSave();
                }
              }}
              style={{
                display: steps[currentStep].showButton ? undefined : 'none',
                minWidth: 213,
                width: 'auto',
                fontSize: '14px'
              }}
              disabled={disableStepButtons || disableSaveButton || hasError}
            >
              {steps[currentStep].saveButtonContent}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressStepper;
